import firebase from 'firebase';
import { call, put } from 'redux-saga/effects';
import { ActionType, createAction, createAsyncAction, createReducer, deprecated } from 'typesafe-actions';

import { NIMBLY_API_USER_LANGUAGE, NIMBLY_API_USER_PROFILE } from '../../../constants/api';
import { LanguageType } from '../../../constants/Languages';
import { fetchLoggedDeviceUUID } from '../../../helpers/loggedDevice';
import { User } from '../../../utils/classes';

const { createStandardAction } = deprecated;

/**
 * The profile, fetched over REST from api-users rather than subscribed to in RTDB.
 *
 * ADR-065 Phase 3. `isLoaded` and `isEmpty` are kept because forty call sites and nine
 * auth gates already read them -- they were react-redux-firebase metadata, and are now
 * synthesized here: `isLoaded` once the request has settled either way, `isEmpty` when no
 * record came back. The initial state deliberately mirrors rrf's, so consumers see the
 * same "unloaded, then loaded" lifecycle they were already written for.
 */
export type Profile = Partial<User> & {
  organization?: string;
  isLoaded: boolean;
  isEmpty: boolean;
};

export interface ProfileState {
  profile: Profile;
  error: string | null;
}

export const FETCH_PROFILE_REQUEST = '@profile/FETCH_PROFILE_REQUEST';
export const FETCH_PROFILE_SUCCESS = '@profile/FETCH_PROFILE_SUCCESS';
export const FETCH_PROFILE_FAILURE = '@profile/FETCH_PROFILE_FAILURE';
export const CLEAR_PROFILE = '@profile/CLEAR_PROFILE';
export const SET_LANGUAGE_REQUEST = '@profile/SET_LANGUAGE_REQUEST';
export const SET_LANGUAGE_SUCCESS = '@profile/SET_LANGUAGE_SUCCESS';
export const SET_LANGUAGE_FAILURE = '@profile/SET_LANGUAGE_FAILURE';

export const fetchProfileAsync = createAsyncAction(
  FETCH_PROFILE_REQUEST,
  FETCH_PROFILE_SUCCESS,
  FETCH_PROFILE_FAILURE
)<{ userID: string }, { user: User }, { error: string }>();

export const clearProfile = createStandardAction(CLEAR_PROFILE)();

export const setLanguageAsync = createAsyncAction(
  SET_LANGUAGE_REQUEST,
  SET_LANGUAGE_SUCCESS,
  SET_LANGUAGE_FAILURE
)<{ language: LanguageType }, { language: LanguageType }, { error: string }>();

export function* fetchProfileSaga(action: ReturnType<typeof fetchProfileAsync.request>) {
  try {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) {
      throw new Error('no authenticated user');
    }

    // The token is passed explicitly rather than relying on ambient auth: at cold start
    // this can run before any interceptor is primed.
    const authToken = (yield currentUser.getIdToken()) as string;
    const request = () =>
      fetch(NIMBLY_API_USER_PROFILE(action.payload.userID), {
        headers: { authorization: authToken },
        method: 'GET'
      });

    const response = (yield call(request)) as Body & { ok: boolean; status: number };
    const body = (yield response.json()) as { data?: User; message?: string; error?: string };

    if (!response.ok || !body?.data) {
      throw new Error(body?.error || body?.message || `profile fetch failed (${response.status})`);
    }

    // ADR-065 Phase 4: the device uuid now comes from Mongo's live device identity rather
    // than a one-shot RTDB read of `/user/{uid}`. Mongo's *top-level* uuid/deviceInfo are
    // legacy and unmaintained, which is why this reads the loggedDevice record instead --
    // the same place the login and logout writes now go, so the concurrent-login check
    // still compares like with like. Only the uuid is read: nothing here consumes
    // `deviceInfo`.
    let device: { uuid?: string } = {};
    try {
      const uuid = (yield call(fetchLoggedDeviceUUID, authToken)) as string;
      device = { uuid };
    } catch (deviceError) {
      // Non-fatal: without a uuid the concurrent-login check short-circuits on its
      // `profile.uuid &&` guard, which fails safe rather than prompting spuriously.
    }

    yield put(fetchProfileAsync.success({ user: { ...body.data, ...device } as User }));
  } catch (error) {
    // Deliberately not a soft failure. Leaving the app running with an empty profile would
    // let a user through the auth gates with no organization, and every org-scoped query
    // would then run unscoped. Reporting empty sends them back to login instead, which is
    // what audit-lite does with the same failure.
    yield put(fetchProfileAsync.failure({ error: (error as Error).message }));
  }
}

/** The shape consumers see before the fetch settles, and after a sign-out. */
export const PROFILE_UNLOADED: Profile = { isLoaded: false, isEmpty: true };

const initialState: ProfileState = {
  profile: PROFILE_UNLOADED,
  error: null
};

export type ProfileAction = ActionType<
  typeof fetchProfileAsync | typeof setLanguageAsync | typeof clearProfile | typeof createAction
>;

export const profileReducer = createReducer<ProfileState, ProfileAction>(initialState)
  .handleAction(fetchProfileAsync.request, state => ({
    ...state,
    error: null
  }))
  .handleAction(fetchProfileAsync.success, (state, action) => {
    const user = action.payload.user;
    return {
      profile: {
        ...user,
        // RTDB called this `organization`; Mongo canonically calls it `organizationID` and
        // carries both. Consumers read `organization`, so resolve it here once.
        organization: (user as any).organization ?? (user as any).organizationID,
        isLoaded: true,
        isEmpty: false
      },
      error: null
    };
  })
  .handleAction(fetchProfileAsync.failure, (state, action) => ({
    profile: { isLoaded: true, isEmpty: true },
    error: action.payload.error
  }))
  // Applied straight away rather than on success, matching the optimistic update the
  // previous react-redux-firebase write gave us.
  .handleAction(setLanguageAsync.request, (state, action) => ({
    ...state,
    profile: { ...state.profile, language: action.payload.language }
  }))
  .handleAction(setLanguageAsync.failure, (state, action) => ({
    ...state,
    error: action.payload.error
  }))
  .handleAction(clearProfile, () => initialState);

export default profileReducer;

/**
 * Persist the language choice. Previously `updateProfile({ language })` wrote it to RTDB
 * and the subscription fed it back; now it goes to api-users, which writes Mongo and still
 * mirrors to RTDB server-side.
 */
export function* setLanguageSaga(action: ReturnType<typeof setLanguageAsync.request>) {
  const { language } = action.payload;
  try {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) {
      throw new Error('no authenticated user');
    }
    const authToken = (yield currentUser.getIdToken()) as string;
    const request = () =>
      fetch(NIMBLY_API_USER_LANGUAGE, {
        method: 'POST',
        headers: { authorization: authToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ language })
      });

    const response = (yield call(request)) as Body & { ok: boolean; status: number };
    if (!response.ok) {
      const body = (yield response.json()) as { error?: string; message?: string };
      throw new Error(body?.error || body?.message || `language update failed (${response.status})`);
    }

    yield put(setLanguageAsync.success({ language }));
  } catch (error) {
    yield put(setLanguageAsync.failure({ error: (error as Error).message }));
  }
}
